"use client";

import AmazingDialog from "@/components/Dialogs/AmazingDialog";
import ErrorBoundary from "@/components/ErrorBoundary";
import { MobileWarningBanner } from "@/components/Layouts/MobileWarningBanner";
import Authorization, { PAGES_AUTHORIZATION } from "@/components/Authorization";
import { cn } from "@/lib/utils";
import { setPylonBubbleSuppressed } from "@/utils/pylon";
import { SpotUserProvider, useSpotUser } from "@/utils/spotUser";
import { currentUserContext } from "@/lib/genie/user-context";
import { motion } from "framer-motion";
import Head from "next/head";
import { usePathname, redirect } from "next/navigation";
import { ReactNode, Suspense, useEffect, useRef, useState } from "react";
import { GenieStageContainerProvider } from "@/lib/genie/stage-container";
import PlatformDialogs from "@/utils/providers/PlatformDialogs";
import { atomWithStorage } from "jotai/utils";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useIsSidenavCollapsible } from "@/components/Spotflow/SideNav/useIsSidenavCollapsible";
import { sidenavRevealAtom } from "@/components/Spotflow/SideNav/atoms";
import { useCommandPalette } from "@/hooks/useCommandPalette";
import { TemplateSideNav } from "@/components/Layouts/ToolLayout/TemplateSideNav";
import { resolveSidenav } from "@/components/Spotflow/SideNav/resolveSidenav";
import { DashboardSidenavSlot } from "@/components/Spotflow/SideNav/DashboardSidenavSlot";
import { TemplateDeploymentLoading } from "@/components/Templates/TemplateDeploymentLoading";
import { isTemplateDeploymentLoadingAtom } from "@/store/editor-jotai";
import {
  DeploymentToolbar,
  HARDCORE_KEY,
  HARDCORE_CHANGED_EVENT,
} from "@/components/DeploymentToolbar/DeploymentToolbar";
import { WelcomeModal } from "@/components/Modals/WelcomeModal";
import {
  FeatureAnnouncementModal,
  hasUnseenAnnouncement,
} from "@/components/Modals/FeatureAnnouncementModal";
import { UsersService } from "@/generated";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DATA_KEYS } from "@/utils/useRemoteData";
import Authenticated from "@/components/Authenticated";
import { GeniePanel } from "@/components/Genie/GeniePanel";
import { GenieToggleButton } from "@/components/Genie/GenieToggleButton";
import { GenieSpotlight } from "@/components/Genie/GenieSpotlight";
import { useIsGenieEnabled } from "@/lib/genie/useIsGenieEnabled";
import { useGenieContext } from "@/lib/genie/use-genie-context";
import { usePermissions } from "@/hooks/usePermissions";
import { P } from "@/constants/permissions";
import { usePlatformTools } from "@/lib/genie/modules/platform";
import { usePastChatsTools } from "@/lib/genie/modules/past-chats";
import { useWorkspaceTools } from "@/lib/genie/modules/workspaces";
import { useGenieSurfaces } from "@/lib/genie/use-genie-surfaces";
import { genieOpenAtom } from "@/store/genie-atoms";
import { isEditorV2Enabled } from "@/utils/modules";
import { importCsvFileAtom, isCreateTableOpenAtom } from "@/store/ui-atoms";
import { CreateTableDialog } from "@/components/TableTool/CreateTableDialog";
import { ImportCsvDialog } from "@/components/TableTool/ImportCsvDialog";

// Turned off for now; flip to bring the welcome dialog back on first login.
const SHOW_WELCOME_MODAL = false;

export const templateDisplayOpenAtom = atomWithStorage<boolean>(
  "template-drawer-open",
  false
);

function DashboardLayoutComp({ children }: { children: ReactNode }) {
  const { isGhosting, spotTenant, spotUser, spotGroup } = useSpotUser();
  const [hardcoreGhost, setHardcoreGhost] = useState(false);
  const [stageEl, setStageEl] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    setHardcoreGhost(localStorage.getItem(HARDCORE_KEY) === "true");
    const onCustom = (e: Event) => {
      setHardcoreGhost((e as CustomEvent<boolean>).detail);
    };
    window.addEventListener(HARDCORE_CHANGED_EVENT, onCustom);
    return () => window.removeEventListener(HARDCORE_CHANGED_EVENT, onCustom);
  }, []);

  const pathname = usePathname();
  const { isDialogOpen, handleDialogChange } = useCommandPalette();
  const [importCsvFile, setImportCsvFile] = useAtom(importCsvFileAtom);
  const [isCreateTableOpen, setIsCreateTableOpen] = useAtom(
    isCreateTableOpenAtom
  );

  const editorV2Enabled = isEditorV2Enabled(spotGroup);
  const templatesBlocked =
    editorV2Enabled && Boolean(pathname?.startsWith("/templates"));

  const isGenieEnabled = useIsGenieEnabled();
  const isGenieOpen = useAtomValue(genieOpenAtom) && isGenieEnabled;
  usePlatformTools(spotGroup.id, editorV2Enabled);
  usePastChatsTools(spotGroup.id);
  useWorkspaceTools();
  useGenieSurfaces();

  useGenieContext("current_workspace", "The active workspace", {
    name: spotGroup.name,
    id: spotGroup.id,
  });
  useGenieContext(
    "editor_v2_enabled",
    "Whether this workspace uses editor v2. When false, new workflows are editor-v1 and you cannot edit them: say so instead of trying",
    editorV2Enabled
  );
  useGenieContext(
    "is_case_management",
    "Whether case-management nodes are available in this workspace",
    Boolean(spotGroup.settings?.case_management_enabled)
  );
  useGenieContext(
    "current_user",
    "The logged-in user, and the time zone their browser runs in",
    currentUserContext(spotUser)
  );
  const { hasPermission, isLoading: permissionsLoading } = usePermissions(
    spotGroup.id
  );
  const canEditResources = hasPermission(P.RESOURCE_EDIT, "group");
  useGenieContext(
    "current_permissions",
    "What the user is allowed to do in this workspace: never suggest actions they lack permission for",
    {
      can_edit_resources: canEditResources,
      can_run_resources: hasPermission(P.RESOURCE_RUN, "group"),
      can_manage_users: hasPermission(P.USERS_INVITE, "tenant"),
      can_manage_billing: hasPermission(P.BILLING_MANAGE, "tenant"),
      can_manage_providers: hasPermission(P.PROVIDERS_MANAGE, "tenant"),
      is_org_admin: hasPermission(P.ORG_ADMIN, "tenant"),
    }
  );
  const [isWelcomeModalOpen, setIsWelcomeModalOpen] = useState(false);
  const queryClient = useQueryClient();

  const update_user_mutation = useMutation({
    mutationFn: UsersService.updateMe,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.USER] });
    },
  });

  const isUpdatingUserRef = useRef(false);

  useEffect(() => {
    if (permissionsLoading) return;
    const hasSeenWelcome = spotUser.settings?.not_first_login || false;
    if (spotUser.id && !hasSeenWelcome && !isUpdatingUserRef.current) {
      isUpdatingUserRef.current = true;
      update_user_mutation.mutate({
        body: {
          settings: {
            ...spotUser.settings,
            not_first_login: true,
          },
        },
      });
      if (
        SHOW_WELCOME_MODAL &&
        spotUser.email !== "tests-e2e@noxus-testing.com" &&
        canEditResources &&
        !editorV2Enabled
      ) {
        setIsWelcomeModalOpen(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    spotUser.settings?.not_first_login,
    spotUser.id,
    permissionsLoading,
    canEditResources,
    editorV2Enabled,
  ]);

  if (templatesBlocked) {
    redirect("/");
  }

  // Tool and app pages are known from the path alone.
  const sidenav = resolveSidenav(pathname ?? "", null);
  const isToolPage = sidenav.kind === "tool";

  const isTemplateDeploymentLoading = useAtomValue(
    isTemplateDeploymentLoadingAtom
  );

  const hidePylon = isToolPage || Boolean(pathname?.startsWith("/chat"));
  useEffect(() => {
    setPylonBubbleSuppressed(hidePylon);
  }, [hidePylon]);

  const isSidenavCollapsible = useIsSidenavCollapsible();
  const setSidenavReveal = useSetAtom(sidenavRevealAtom);

  return (
    <Authenticated>
      <GenieStageContainerProvider value={isGenieOpen ? stageEl : undefined}>
        <div
          className={cn("flex h-screen w-screen overflow-hidden bg-[#F5F6F6]")}
          data-genie-open={isGenieOpen || undefined}
        >
          <motion.div
            ref={setStageEl}
            className="flex flex-1 min-w-0 overflow-hidden bg-background relative shadow-xs border border-ring/40 z-10"
            animate={{
              marginTop: isGenieOpen ? 12 : 0,
              marginBottom: isGenieOpen ? 12 : 0,
              marginLeft: isGenieOpen ? 12 : 0,
              marginRight: 0,
              borderRadius: isGenieOpen ? 12 : 0,
            }}
            transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
            onMouseMove={(e) => {
              if (!isSidenavCollapsible) {
                setSidenavReveal(false);
                return;
              }
              const dx = e.clientX - (isGenieOpen ? 16 : 0);
              // The open sidebar is 16rem: past its edge it closes, with no
              // invisible margin beyond it.
              const sidenavWidth =
                16 *
                parseFloat(getComputedStyle(document.documentElement).fontSize);
              setSidenavReveal(
                (revealed) =>
                  dx >= 0 &&
                  (revealed ? dx < sidenavWidth : dx < 16 && e.buttons === 0)
              );
            }}
            onMouseLeave={() => setSidenavReveal(false)}
          >
            <DashboardSidenavSlot />
            <main
              className={cn(
                "flex-1 min-w-0 bg-background relative flex flex-col overflow-hidden",
                sidenav.kind === "apps" && "border-l border-border"
              )}
            >
              <div
                className="flex-1 min-h-0 flex flex-col overflow-hidden"
                key={spotGroup.id || "loading"}
              >
                {children}
              </div>
            </main>

            <TemplateSideNav />
          </motion.div>
          {isGenieEnabled && (
            <>
              <GeniePanel />
              <GenieToggleButton />
              <GenieSpotlight />
            </>
          )}

          {isGhosting && spotTenant && spotUser.is_admin && (
            <div
              className={cn(
                "fixed inset-0 pointer-events-none z-[9990]",
                hardcoreGhost
                  ? "border-6 border-red-500 animate-pulse"
                  : "border-3 border-red-400"
              )}
            />
          )}

          <AmazingDialog
            isOpen={isDialogOpen}
            onOpenChange={handleDialogChange}
          />

          {isCreateTableOpen && (
            <CreateTableDialog isOpen onOpenChange={setIsCreateTableOpen} />
          )}

          <ImportCsvDialog
            file={importCsvFile}
            isOpen={importCsvFile !== null}
            onOpenChange={(open) => !open && setImportCsvFile(null)}
          />

          <WelcomeModal
            open={isWelcomeModalOpen}
            onOpenChange={setIsWelcomeModalOpen}
          />

          {hasUnseenAnnouncement(spotUser) && <FeatureAnnouncementModal />}

          <TemplateDeploymentLoading isLoading={isTemplateDeploymentLoading} />

          <DeploymentToolbar />
        </div>
      </GenieStageContainerProvider>
    </Authenticated>
  );
}

const DashboardLayout = ({ children }: { children: React.ReactNode }) => {
  const pathname = usePathname();

  const pathnameWithoutIds = pathname?.replace(
    /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/g,
    "{id}"
  );

  const routeGate = PAGES_AUTHORIZATION.find((page) => {
    if (!pathnameWithoutIds || !page?.href) {
      return false;
    }
    return pathnameWithoutIds.includes(page.href);
  });

  return (
    <>
      <Head>
        <title>Noxus</title>
      </Head>
      <SpotUserProvider>
        <Authorization
          requiredPermissions={
            routeGate?.requiredPermissions
              ? [...routeGate.requiredPermissions]
              : undefined
          }
          level={routeGate?.level}
          // Default to "any-of" for route gates: the granular permission
          // model lists alternatives (e.g. RESOURCE_VIEW_PERMS = read |
          // edit | run | delete). Routes that genuinely need ALL listed
          // perms can opt back in via requireAll on the route entry.
          requireAll={
            (routeGate as { requireAll?: boolean })?.requireAll ?? false
          }
        >
          <DashboardLayoutComp>
            <ErrorBoundary>
              <MobileWarningBanner />
              <Suspense fallback={null}>{children}</Suspense>
            </ErrorBoundary>
          </DashboardLayoutComp>
        </Authorization>
        <PlatformDialogs />
      </SpotUserProvider>
    </>
  );
};

export default DashboardLayout;
